/**
 * Etiketten fuer den Labelmaker -- im Browser gezeichnet.
 *
 * Im Browser und nicht auf dem Server, weil an der Einsatzstelle kein Netz
 * ist: Wer dort ein abgerissenes Etikett ersetzt, soll es drucken koennen.
 *
 * Gezeichnet wird in der Aufloesung des Druckers (180 dpi), jedes Code-Modul
 * auf ganze Punkte. So kommt der Code so scharf aus dem Drucker, wie er nur
 * sein kann -- ein verschwommenes DataMatrix liest keine Kamera.
 *
 * - **Lagerorte** bekommen einen QR-Code mit ihrer Adresse: Wer ihn mit der
 *   Handykamera scannt, landet direkt in der WaWi. Name und Kennung stehen
 *   darunter oder daneben.
 * - **Geraete** bekommen einen kleinen DataMatrix mit ihrer THW-Inventarnummer
 *   -- genau der Nummer, die schon auf dem Geraet steht. Eigene Codes vergibt
 *   dieses System fuer Geraete nicht (CLAUDE.md §8).
 */

import type * as BwipModul from "bwip-js/browser";
import type { Lagerort } from "@thw/contracts";
import { lagerortAdresse, pfadKuerzen } from "@thw/core-domain";
import { BAENDER, type Bandbreite, type Bild } from "./ptouch.js";

export type Etikett = (
  | {
      art: "lagerort";
      kennung: string;
      bezeichnung: string;
      /** Adresse im QR-Code, etwa https://wawi.example/l/K7M2Q */
      adresse: string;
    }
  | { art: "geraet"; inventarnummer: string; bezeichnung: string }
) & {
  /**
   * Der Weg zum Stammplatz, etwa `["HALLE", "T7-F1"]` -- als dritte Zeile,
   * nur ab 18 mm Band (ADR 0013 §12, ADR 0017). Leer oder weggelassen: keine.
   */
  stammplatz?: readonly string[] | undefined;
};

/** Ab dieser Bandbreite passt der Stammplatz als dritte Zeile. */
export const STAMMPLATZ_AB_MM = 18;

/**
 * Die Adresse im QR-Code eines Lagerorts: der feste Kurzcode (ADR 0017).
 *
 * Eine lokale Kopie von vor Vertrag 2.8 kennt ihn noch nicht; bis zum
 * naechsten Abgleich steht dann die Kennung darin -- so wie auf allen
 * Etiketten vor dem Kurzcode, und `/l/` findet beides.
 */
export function lagerortQr(
  basis: string,
  lagerort: Pick<Lagerort, "kennung"> & Partial<Pick<Lagerort, "kurzcode">>,
): string {
  return lagerortAdresse(basis, lagerort.kurzcode ?? lagerort.kennung);
}

/** Wo der Name steht: unter dem Code oder daneben. */
export type Anordnung = "darunter" | "daneben";

export interface Matrix {
  breite: number;
  hoehe: number;
  /** 1 = dunkles Modul, zeilenweise */
  module: ArrayLike<number>;
}

type BwipJs = typeof BwipModul;
let bwip: Promise<BwipJs> | null = null;

/** bwip-js ist gross -- erst laden, wenn jemand Etiketten druckt. */
function bwipLaden(): Promise<BwipJs> {
  bwip ??= import("bwip-js/browser").then((m) => (m as { default?: BwipJs }).default ?? m);
  return bwip;
}

export async function codeMatrix(typ: "qrcode" | "datamatrix", text: string): Promise<Matrix> {
  const b = await bwipLaden();
  // QR mit Fehlerkorrektur M: verzeiht einen Kratzer, bleibt bei einer so
  // kurzen Adresse klein. DataMatrix hat seine Korrektur fest eingebaut.
  const roh = b.raw(typ, text, typ === "qrcode" ? "eclevel=M" : "");
  const erstes = roh[0];
  if (erstes === undefined || !("pixs" in erstes)) {
    throw new Error(`Aus „${text}“ ließ sich kein Code erzeugen`);
  }
  return { breite: erstes.pixx, hoehe: erstes.pixy, module: erstes.pixs };
}

/** Kuerzt Text, bis er in die Breite passt. */
function passend(ctx: CanvasRenderingContext2D, text: string, maxBreite: number): string {
  if (ctx.measureText(text).width <= maxBreite) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxBreite) t = t.slice(0, -1);
  return `${t}…`;
}

const SCHRIFT = 'system-ui, "Segoe UI", Roboto, Arial, sans-serif';

/** Druckpunkte fuer eine Laenge in Millimetern. */
const mm = (wert: number): number => Math.round((wert / 25.4) * 180);

/**
 * Zeichnet ein Etikett fuer ein Band.
 *
 * Ergebnis ist genau so hoch, wie das Band bedruckbar ist (24 mm: 128 Punkte),
 * und so lang, wie der Inhalt braucht.
 */
export async function etikettZeichnen(
  etikett: Etikett,
  band: Bandbreite,
  anordnung: Anordnung,
): Promise<Bild> {
  const hoehe = BAENDER[band].druckPunkte;
  const matrix =
    etikett.art === "lagerort"
      ? await codeMatrix("qrcode", etikett.adresse)
      : await codeMatrix("datamatrix", etikett.inventarnummer);
  // Ruhezone: QR braucht Luft, DataMatrix kommt mit einem Modul aus.
  const ruhe = etikett.art === "lagerort" ? 2 : 1;

  const zeile1 = etikett.art === "lagerort" ? etikett.kennung : etikett.inventarnummer;
  const zeile2 = etikett.bezeichnung;

  // Messen geht nur mit einer Leinwand.
  const mess = document.createElement("canvas").getContext("2d");
  if (mess === null) throw new Error("Der Browser kann nicht zeichnen");

  // Der Stammplatz ist die dritte Zeile neben dem Code -- erst ab 18 mm, auf
  // schmalerem Band waere sie nicht mehr lesbar.
  const stammplatz =
    band >= STAMMPLATZ_AB_MM && etikett.stammplatz !== undefined && etikett.stammplatz.length > 0
      ? etikett.stammplatz
      : null;

  // „Darunter" braucht Hoehe fuer eine Textzeile -- auf schmalen Baendern
  // bleibt dafuer zu wenig; dann steht der Name daneben. Mit dem Stammplatz
  // auch: Zwei Zeilen unter dem Code liessen ihm auf 18 mm ein Druckpunkt je
  // Modul, und so einen QR liest keine Kamera.
  const darunter =
    anordnung === "darunter" && etikett.art === "lagerort" && band >= 18 && stammplatz === null;

  let codeKante: number;
  let modul: number;
  let breite: number;
  let text: { zeile: string; x: number; y: number; groesse: number; fett: boolean }[];

  if (darunter) {
    const textHoehe = Math.round(hoehe * 0.26);
    const verfuegbar = hoehe - textHoehe - 2;
    modul = Math.max(1, Math.floor(verfuegbar / (matrix.breite + 2 * ruhe)));
    codeKante = modul * (matrix.breite + 2 * ruhe);
    const groesse = Math.round(textHoehe * 0.92);
    mess.font = `bold ${String(groesse)}px ${SCHRIFT}`;
    const inhalt = passend(mess, `${zeile1}  ${zeile2}`, mm(70));
    const textBreite = Math.ceil(mess.measureText(inhalt).width);
    breite = Math.max(codeKante, textBreite) + 8;
    text = [
      {
        zeile: inhalt,
        x: Math.round((breite - textBreite) / 2),
        y: hoehe - 2,
        groesse,
        fett: true,
      },
    ];
  } else {
    modul = Math.max(1, Math.floor(hoehe / (matrix.breite + 2 * ruhe)));
    codeKante = modul * (matrix.breite + 2 * ruhe);
    const drei = stammplatz !== null;
    const lagerort = etikett.art === "lagerort";
    const gross = Math.round(hoehe * (drei ? (lagerort ? 0.34 : 0.3) : lagerort ? 0.4 : 0.34));
    const klein = Math.round(hoehe * (drei ? 0.19 : 0.22));
    const maxText = mm(lagerort ? 60 : 45);

    mess.font = `bold ${String(gross)}px ${SCHRIFT}`;
    const t1 = passend(mess, zeile1, maxText);
    const b1 = mess.measureText(t1).width;
    mess.font = `${String(klein)}px ${SCHRIFT}`;
    const t2 = passend(mess, zeile2, maxText);
    const b2 = mess.measureText(t2).width;
    // Ein zu langer Weg verliert erst die Mitte -- der Platz selbst bleibt.
    const t3 =
      stammplatz === null
        ? ""
        : passend(
            mess,
            `Stammplatz: ${pfadKuerzen(stammplatz, (t) => mess.measureText(`Stammplatz: ${t}`).width <= maxText)}`,
            maxText,
          );
    const b3 = mess.measureText(t3).width;

    const textX = codeKante + mm(1.5);
    breite = Math.ceil(textX + Math.max(b1, b2, b3)) + mm(1);
    // Zwei oder drei Zeilen, zusammen mittig in der Hoehe.
    const block = gross + klein * 1.15 * (drei ? 2 : 1);
    const oben = Math.round((hoehe - block) / 2);
    text = [
      { zeile: t1, x: textX, y: oben + gross * 0.85, groesse: gross, fett: true },
      { zeile: t2, x: textX, y: oben + gross + klein * 0.95, groesse: klein, fett: false },
    ];
    if (drei) {
      text.push({
        zeile: t3,
        x: textX,
        y: oben + gross + klein * 1.15 + klein * 0.95,
        groesse: klein,
        fett: false,
      });
    }
  }

  // Mindestlaenge laut Handbuch: 31 Punkt.
  breite = Math.max(breite, 31);

  const leinwand = document.createElement("canvas");
  leinwand.width = breite;
  leinwand.height = hoehe;
  const ctx = leinwand.getContext("2d", { willReadFrequently: true });
  if (ctx === null) throw new Error("Der Browser kann nicht zeichnen");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, breite, hoehe);
  ctx.fillStyle = "#000";

  // Der Code: jedes Modul ein Quadrat aus ganzen Punkten.
  const codeX = darunter ? Math.round((breite - codeKante) / 2) : 0;
  const codeY = darunter ? 0 : Math.round((hoehe - codeKante) / 2);
  for (let y = 0; y < matrix.hoehe; y += 1) {
    for (let x = 0; x < matrix.breite; x += 1) {
      if (matrix.module[y * matrix.breite + x] === 1) {
        ctx.fillRect(codeX + (ruhe + x) * modul, codeY + (ruhe + y) * modul, modul, modul);
      }
    }
  }

  for (const t of text) {
    ctx.font = `${t.fett ? "bold " : ""}${String(t.groesse)}px ${SCHRIFT}`;
    ctx.textBaseline = "alphabetic";
    ctx.fillText(t.zeile, t.x, t.y);
  }

  // In reines Schwarz-Weiss: Der Drucker kennt nur „heiss" oder „nicht".
  const pixel = ctx.getImageData(0, 0, breite, hoehe).data;
  const punkte = new Uint8Array(breite * hoehe);
  for (let i = 0; i < punkte.length; i += 1) {
    const hell = (pixel[i * 4] ?? 255) + (pixel[i * 4 + 1] ?? 255) + (pixel[i * 4 + 2] ?? 255);
    punkte[i] = hell < 3 * 140 ? 1 : 0;
  }
  return { breite, hoehe, punkte };
}

/** Ein Bild als Leinwand -- fuer die Vorschau und fuer PNG und PDF. */
export function alsLeinwand(bild: Bild, massstab = 1): HTMLCanvasElement {
  const leinwand = document.createElement("canvas");
  leinwand.width = bild.breite * massstab;
  leinwand.height = bild.hoehe * massstab;
  const ctx = leinwand.getContext("2d");
  if (ctx === null) return leinwand;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, leinwand.width, leinwand.height);
  ctx.fillStyle = "#000";
  for (let y = 0; y < bild.hoehe; y += 1) {
    for (let x = 0; x < bild.breite; x += 1) {
      if (bild.punkte[y * bild.breite + x] === 1) {
        ctx.fillRect(x * massstab, y * massstab, massstab, massstab);
      }
    }
  }
  return leinwand;
}

/** Laenge des Etiketts in Millimetern, mit den 2 mm Rand je Seite. */
export function laengeMm(bild: Bild): number {
  return Math.round((bild.breite / 180) * 25.4 + 4);
}
